/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { ProjectResource, ActionTemplateSearchResource, GitRefResource } from "@octopusdeploy/octopus-server-client";
import { Permission, TenantedDeploymentMode, StartTrigger, SpecialVariables } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { isMatchingProcessTemplate } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useProcessBlueprintsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessBlueprintsContextProvider";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import { useOptionalProjectContext } from "~/areas/projects/context";
import Chip from "~/components/Chips/Chip";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import getActionLogoUrl from "../../getActionLogoUrl";
import { useProcessContext } from "../Contexts/ProcessContext";
import { useProcessErrorSelectors } from "../Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessWarningSelectors } from "../Contexts/ProcessWarnings/ProcessWarningsContext";
import type { ProcessListLayoutLoaderLookupData } from "../ProcessListLayoutLoader";
import type { StoredStep, StoredAction } from "../types";
import { isDeploymentOrRunbookProcessIdentifier } from "../types";
import ProcessListItem from "./ProcessListItem";
import processListItemStyles from "./ProcessListItem.module.less";
function getTags(action: StoredAction, project: ProjectResource | undefined): string[] {
    if (project && project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted) {
        return [];
    }
    return action.TenantTags;
}
function getTenantTagsVariable(action: StoredAction, project: ProjectResource | undefined): string | null {
    if (project && project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted) {
        return null;
    }
    return action.TenantTagsVariable;
}
interface ProcessListPageListItemForActionProps {
    lookups: ProcessListLayoutLoaderLookupData;
    actionTemplates: ActionTemplateSearchResource[];
    step: StoredStep;
    action: StoredAction;
    isChildAction: boolean;
    gitRefResource: GitRefResource | undefined;
}
const ProcessListPageListItemForAction: React.FC<ProcessListPageListItemForActionProps> = ({ lookups, actionTemplates, step, action, isChildAction, gitRefResource }) => {
    const projectContext = useOptionalProjectContext();
    const processTemplates = useProcessBlueprintsFromContext();
    const processTemplate = processTemplates.find((pt) => isMatchingProcessTemplate(pt, getProcessTemplateSlugFromAction(action), getProcessTemplateVersionMaskFromAction(action)));
    const { selectors, state: { processIdentifier }, } = useProcessContext();
    let actionTypeName = action.ActionType;
    const actionTemplate = actionTemplates.find((x) => x.Type === action.ActionType);
    if (actionTemplate) {
        actionTypeName = actionTemplate.Name;
    }
    const stepNumber = selectors.getStepNumber(step.Id);
    const actionNumber = selectors.getActionNumber(action.Id);
    const processErrorSelectors = useProcessErrorSelectors();
    const errors = processErrorSelectors.getActionErrors(action.Id, selectors);
    const processWarningSelectors = useProcessWarningSelectors();
    const warnings = processWarningSelectors.getActionWarnings(action.Id, selectors);
    const feedViewPermissionGranted = isAllowed({
        permission: Permission.FeedView,
        project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
        wildcard: true,
    });
    const environmentsById = lookups.environmentsV2ById;
    const channelsById = lookups.channelsById;
    return (<ProcessListItem actionType={actionTypeName} logoUrl={getActionLogoUrl(action, processTemplate?.processTemplate.Id)} environmentsLookup={environmentsById
            ? action.Environments.map((key) => ({
                Key: key,
                Environment: environmentsById[key],
            }))
            : undefined} excludedEnvironmentsLookup={environmentsById
            ? action.ExcludedEnvironments.map((key) => ({
                Key: key,
                Environment: environmentsById[key],
            }))
            : undefined} environmentsVariable={action.EnvironmentsVariable} excludedEnvironmentsVariable={action.ExcludedEnvironmentsVariable} channelsLookup={channelsById
            ? action.Channels.map((key) => ({
                Key: key,
                Channel: channelsById[key],
            }))
            : undefined} channelsVariable={action.ChannelsVariable} tags={getTags(action, projectContext?.state.model)} tenantTagsVariable={getTenantTagsVariable(action, projectContext?.state.model)} isDisabled={selectors.isActionDisabled(action.Id)} notes={action.Notes} actionErrors={errors} actionWarnings={warnings} index={`${isChildAction ? `${stepNumber}.${actionNumber}.` : `${stepNumber}.`}`} detailsUrl={selectors.getStepDetailsUrl(gitRefResource, step.Id, action.Id)} isParentGroup={false} name={action.Name} isRunInParallelWithLast={step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isChildAction(action.Id) && !selectors.isFirstStep(step.Id)}>
            <div className={processListItemStyles.summaryContainer}>
                {action.plugin.summary(action.Properties, isChildAction ? "" : (step.Properties[SpecialVariables.Action.TargetRoles] as string), action.Packages, action.WorkerPoolId ? lookups.workerPoolsById[action.WorkerPoolId] : undefined, action.WorkerPoolVariable ?? undefined, action.GitDependencies ?? undefined)}
                {action.Container.Image && action.Container.FeedId && action.Properties["Octopus.Action.RunOnServer"] === "true" ? (<span style={{ paddingLeft: "0.1em" }}>
                        <Chip description={feedViewPermissionGranted ? `Container Image: ${action.Container.Image}` : undefined}>Runs in a container</Chip>
                    </span>) : ("")}
            </div>
        </ProcessListItem>);
};
ProcessListPageListItemForAction.displayName = "ProcessListPageListItemForAction"
export default ProcessListPageListItemForAction;
